import { Button, Card, Input, Tooltip } from '@ibsys/kit';
import { HelpCircleIcon, UploadIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColorParameterBlock, ThemeValue } from '@/entities/themes';
import { useDeleteTheme, useUpdateTheme } from '@/entities/themes';
import { useSettings } from '@/entities/settings';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { getHexAlpha, getHexWithoutAlpha, hexToHsl, isFullyTransparent } from '../lib/colorUtils';
import { ColorParameterCard } from './ColorParameterCard';
import { UploadLogoFaviconDialog } from './UploadLogoFaviconDialog';

const TRANSPARENT_HEX = '#00000000';
const DEFAULT_THEME_CODE = 'light';

type ThemeCardProps = {
  theme: ThemeValue;
  colorBlocks: ColorParameterBlock[];
  canEdit: boolean;
  favicon?: string;
  onDeleted?: () => void;
};

type ColorFormValue = {
  parameterCode: string;
  colorHex: string;
  colorHslH: string;
  colorHslS: string;
  colorHslL: string;
  colorHslA?: string;
};

export const ThemeCard: FC<ThemeCardProps> = ({ theme, colorBlocks, canEdit, favicon, onDeleted }) => {
  const { t } = useTranslation(['common', 'themes']);
  const [editedName, setEditedName] = useState(theme.name);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const { theme: currentTheme, setTheme } = useSettings();
  const { mutateAsync: updateTheme } = useUpdateTheme();
  const { mutateAsync: deleteThemeMutation, isPending: isDeleting } = useDeleteTheme();

  const colorValuesMap = useMemo(() => {
    const map: Record<string, ColorFormValue> = {};
    for (const value of theme.colorParameterValues) {
      map[value.parameterCode] = {
        parameterCode: value.parameterCode,
        colorHex: value.colorHex,
        colorHslH: value.colorHslH,
        colorHslS: value.colorHslS,
        colorHslL: value.colorHslL,
        colorHslA: value.colorHslA,
      };
    }
    return map;
  }, [theme.colorParameterValues]);

  const getColorValue = useCallback(
    (code: string): ColorFormValue | undefined => {
      return colorValuesMap[code];
    },
    [colorValuesMap],
  );

  const buildAllColors = useCallback(
    (updatedCode?: string, updatedHex?: string): ColorFormValue[] => {
      const allColors: ColorFormValue[] = [];

      for (const block of colorBlocks) {
        for (const param of block.colorParameter) {
          const value = colorValuesMap[param.code];
          if (value) {
            if (updatedCode === param.code && updatedHex !== undefined) {
              const hexToSave = isFullyTransparent(updatedHex) ? TRANSPARENT_HEX : updatedHex;
              const hex6 = getHexWithoutAlpha(hexToSave);
              const alpha = getHexAlpha(hexToSave);
              const hsl = hexToHsl(hex6);
              allColors.push({
                parameterCode: param.code,
                colorHex: hexToSave,
                colorHslH: hsl.h.toString(),
                colorHslS: hsl.s.toString(),
                colorHslL: hsl.l.toString(),
                colorHslA: String(alpha),
              });
            } else {
              allColors.push(value);
            }
          }
        }
      }
      return allColors;
    },
    [colorBlocks, colorValuesMap],
  );

  const handleNameBlur = useCallback(async () => {
    if (editedName !== theme.name && editedName.trim()) {
      await updateTheme({
        id: theme.id,
        body: {
          code: theme.code,
          logoSvg: theme.logoSvg,
          name: editedName.trim(),
          isActive: theme.isActive,
          colorParameterValues: buildAllColors(),
        },
      });
    }
  }, [editedName, theme, updateTheme, buildAllColors]);

  const handleColorChange = useCallback(
    async (code: string, hex: string) => {
      const currentValue = colorValuesMap[code];
      if (currentValue?.colorHex === hex) {
        return;
      }
      await updateTheme({
        id: theme.id,
        body: {
          code: theme.code,
          logoSvg: theme.logoSvg,
          name: theme.name,
          isActive: theme.isActive,
          colorParameterValues: buildAllColors(code, hex),
        },
      });
    },
    [theme, updateTheme, buildAllColors, colorValuesMap],
  );

  const handleDeleteTheme = useCallback(async () => {
    if (currentTheme === theme.code) {
      setTheme(DEFAULT_THEME_CODE);
    }
    await deleteThemeMutation(theme.id);
    onDeleted?.();
  }, [currentTheme, theme.code, theme.id, setTheme, deleteThemeMutation, onDeleted]);

  const handleDeleteLogo = useCallback(async () => {
    await updateTheme({
      id: theme.id,
      body: {
        code: theme.code,
        logoSvg: '',
        name: theme.name,
        isActive: theme.isActive,
        colorParameterValues: buildAllColors(),
      },
    });
  }, [theme, updateTheme, buildAllColors]);

  const handleDeleteFavicon = useCallback(async () => {
    await updateTheme({
      id: theme.id,
      body: {
        code: theme.code,
        logoSvg: theme.logoSvg,
        name: theme.name,
        isActive: theme.isActive,
        favicon: null,
        colorParameterValues: buildAllColors(),
      },
    });
  }, [theme, updateTheme, buildAllColors]);

  const isSystemTheme = theme.isSystem === true;

  return (
    <div className="flex flex-col gap-6">
      <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Миниатюра логотипа с удалением при наведении */}
            {theme.logoSvg && (
              <div className="relative group">
                <img src={theme.logoSvg} alt={`${theme.name} logo`} className="h-12 max-w-48 object-contain" />
                {canEdit && (
                  <div
                    role="button"
                    tabIndex={-1}
                    onClick={handleDeleteLogo}
                    className="absolute -top-2 -right-2 hidden group-hover:flex items-center justify-center size-5 rounded-full bg-destructive text-destructive-foreground cursor-pointer"
                  >
                    <XIcon className="size-3" />
                  </div>
                )}
              </div>
            )}

            {/* Миниатюра favicon с удалением при наведении */}
            {favicon && (
              <div className="relative group">
                <img src={favicon} alt="favicon" className="size-8 object-contain" />
                {canEdit && (
                  <div
                    role="button"
                    tabIndex={-1}
                    onClick={handleDeleteFavicon}
                    className="absolute -top-2 -right-2 hidden group-hover:flex items-center justify-center size-5 rounded-full bg-destructive text-destructive-foreground cursor-pointer"
                  >
                    <XIcon className="size-3" />
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1">
              {canEdit ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onBlur={handleNameBlur}
                    placeholder={t('themes:themeName')}
                    className="w-64"
                  />
                  {!isSystemTheme && (
                    <ConfirmationButton
                      onConfirm={handleDeleteTheme}
                      isLoadingConfirm={isDeleting}
                      title={t('themes:deleteThemeTitle')}
                      description={t('themes:deleteThemeDescription', { name: theme.name })}
                    />
                  )}
                  {isSystemTheme && (
                    <Tooltip tooltip={t('themes:systemThemeCannotDelete')}>
                      <span className="text-xs text-muted-foreground">{t('themes:systemThemeCannotDelete')}</span>
                    </Tooltip>
                  )}
                </div>
              ) : (
                <h2 className="text-xl font-semibold">{theme.name}</h2>
              )}
              <span className="text-sm text-muted-foreground">
                {t('themes:themeCodeWithValue', { code: theme.code })}
              </span>
            </div>
          </div>

          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<UploadIcon className="size-4" />}
              onClick={() => setIsUploadDialogOpen(true)}
            >
              {t('themes:uploadLogoAndFavicon')}
            </Button>
          )}
        </div>
      </Card>

      <UploadLogoFaviconDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        theme={theme}
      />

      {colorBlocks.map((block) => (
        <Card key={block.code} className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-medium">{block.name}</h3>
            {block.description && (
              <Tooltip tooltip={block.description}>
                <HelpCircleIcon className="size-4 text-muted-foreground cursor-help" />
              </Tooltip>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {block.colorParameter.map((param) => {
              const colorValue = getColorValue(param.code);
              const item = {
                ...param,
                colorHex: colorValue?.colorHex ?? '#000000',
                colorHslH: colorValue?.colorHslH ?? '0',
                colorHslS: colorValue?.colorHslS ?? '0',
                colorHslL: colorValue?.colorHslL ?? '0',
                colorHslA: colorValue?.colorHslA,
              };
              return (
                <ColorParameterCard
                  key={param.code}
                  item={item}
                  canEdit={canEdit}
                  onColorChange={(hex) => handleColorChange(param.code, hex)}
                />
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
};
